/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import { TextField } from '@material-ui/core';
import { Change, NetworkPolicyGraph } from './types';

type Props = {
  open: boolean;
  service: string;
  graph: NetworkPolicyGraph;
  traefikAlreadyActive?: boolean;
  onClose: () => void;
  onStage: (change: Change) => void;
};

/**
 * The first-policy flow: enabling default-deny ingress must carry the FULL
 * list of current callers, because everything not listed is cut off at merge.
 * Suggestions come from the graph (services already declaring they call this
 * one via `calls` would be ideal; today the operator picks from all services),
 * but the operator confirms completeness explicitly.
 */
type Caller = { name: string; namespace: string };

export const EnablePolicyDialog = ({
  open,
  service,
  graph,
  traefikAlreadyActive,
  onClose,
  onStage,
}: Props) => {
  // Selection holds full {name, namespace} objects captured at pick time,
  // NOT names re-resolved against the live graph at stage time. The graph
  // prop can refresh in the background while the dialog is open; if a node
  // momentarily drops out of it (transient fetch), a name-based re-resolve
  // would silently omit that attested caller from the enablePolicy change,
  // cutting it off at enforcement while the "covers ALL callers" checkbox
  // stays ticked. Capturing at selection makes stage() a pure passthrough.
  const [selected, setSelected] = useState<Caller[]>([]);
  const [envs, setEnvs] = useState<string[]>([]);
  const [confirmed, setConfirmed] = useState(false);

  const candidates = useMemo(
    () =>
      graph.nodes
        .filter(n => n.name !== service && n.namespace)
        .map(n => ({ name: n.name, namespace: n.namespace! })),
    [graph, service],
  );

  // Envs the backend reports for this service (one values file each).
  const envOptions = useMemo(
    () => graph.nodes.find(n => n.name === service)?.envs ?? [],
    [graph, service],
  );

  // Reset only on the open transition (false -> true), not on every
  // envOptions change. envOptions is derived from the graph prop, so a
  // background graph refresh while the dialog is open (e.g. triggered by a
  // submit elsewhere on the page) would otherwise produce a new array
  // reference and silently wipe the in-progress caller selection and the
  // completeness attestation. wasOpen is a ref (not reactive) purely to
  // detect the transition; envOptions is captured at that moment via the
  // closure, not re-applied on later renders while still open.
  //
  // Convention: NetworkPolicy config is identical across all envs by
  // default, and enforcement is now on in every platform environment, so
  // treat every selected env as live. enablePolicy writes the full attested
  // caller list, so defaulting to all envs is safe by construction;
  // unchecking an env is the opt-out.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setSelected([]);
      setConfirmed(false);
      setEnvs(envOptions);
    }
    wasOpen.current = open;
  }, [open, envOptions]);

  const stage = () => {
    onStage({ op: 'enablePolicy', service, callers: selected, envs });
    setSelected([]);
    setConfirmed(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Enable network policy on {service}</DialogTitle>
      <DialogContent>
        <Alert severity="warning">
          {traefikAlreadyActive ? (
            <>
              This adds a <b>peer allow-list</b> (calledBy) for <b>{service}</b>{' '}
              in the selected environments
              {envs.length > 0 && (
                <>
                  {' '}
                  (<b>{envs.join(', ')}</b>)
                </>
              )}
              . Chart autoLockdown already admits Traefik + baseline; only the
              callers listed below will be able to reach it as in-cluster peers
              once the PR merges. <b>Every peer you forget gets cut off.</b>
            </>
          ) : (
            <>
              This flips <b>{service}</b> to <b>default-deny ingress</b> in the
              selected environments
              {envs.length > 0 && (
                <>
                  {' '}
                  (<b>{envs.join(', ')}</b>)
                </>
              )}
              . Only the callers listed below (plus the platform baseline: metrics
              scrapers, Traefik, probes) will be able to reach it once the PR
              merges. <b>Every caller you forget gets cut off.</b>
            </>
          )}
        </Alert>
        <Typography variant="body2" style={{ marginTop: 12 }}>
          List every service that currently calls {service}. Callers that are
          not catalog services (batch jobs, third-party namespaces) are NOT
          shown here, add those to the values file in the PR by hand.
        </Typography>
        <Typography variant="subtitle2" style={{ marginTop: 12 }}>
          Environments
        </Typography>
        <FormGroup row>
          {envOptions.map(env => (
            <FormControlLabel
              key={env}
              control={
                <Checkbox
                  size="small"
                  checked={envs.includes(env)}
                  onChange={e =>
                    setEnvs(prev =>
                      e.target.checked
                        ? [...prev, env]
                        : prev.filter(x => x !== env),
                    )
                  }
                />
              }
              label={env}
            />
          ))}
        </FormGroup>
        <Typography variant="caption" color="textSecondary">
          NetworkPolicy config is identical across all envs by
          default, and enforcement is now on in every platform environment,
          so treat every selected env as live: any caller you omit is cut
          off.
        </Typography>
        {envs.length < envOptions.length && (
          <Alert severity="warning" style={{ marginTop: 8 }}>
            Unchecking an env means it will not get this policy, diverging
            from the all-envs-identical convention. Diff and re-sync the
            excluded env(s) so they don't break when reached under
            enforcement:{' '}
            {envOptions.filter(e => !envs.includes(e)).join(', ')}.
          </Alert>
        )}
        <Autocomplete
          multiple
          options={candidates}
          getOptionLabel={o => o.name}
          getOptionSelected={(o, v) =>
            o.name === v.name && o.namespace === v.namespace
          }
          value={selected}
          onChange={(_, v) => setSelected(v)}
          style={{ marginTop: 12 }}
          renderInput={params => (
            <TextField {...params} label="Current callers" placeholder="svc-…" />
          )}
        />
        <FormControlLabel
          style={{ marginTop: 8 }}
          control={
            <Checkbox
              checked={confirmed}
              onChange={e => setConfirmed(e.target.checked)}
            />
          }
          label={`I have verified this list covers ALL current callers of ${service}`}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!confirmed || selected.length === 0 || envs.length === 0}
          onClick={stage}
        >
          Stage enablement
        </Button>
      </DialogActions>
    </Dialog>
  );
};
